import { ArrayBufferTarget, Muxer } from 'mp4-muxer';
import { clipTimeSec, notify, state } from './state.ts';
import { composite, exportDims } from './compositor.ts';
import { seekExact } from './seek.ts';
import { pause } from './timeline.ts';

/**
 * Offline frame-by-frame MP4 export: WebCodecs VideoEncoder (H.264) feeding
 * mp4-muxer. Ported from aka-mocap-viewer's exportVideo.ts. Deterministic —
 * every output frame seeks the sources exactly, then draws the same
 * composite() the preview shows, so output matches the preview frame-for-frame
 * regardless of machine load.
 */

export function isVideoExportSupported(): boolean {
  return typeof window !== 'undefined' && 'VideoEncoder' in window && window.isSecureContext;
}

/**
 * AVC levels by max luma sample count (coded area). The level caps the allowed
 * resolution, so pick one large enough or VideoEncoder.configure() fails.
 */
const AVC_LEVELS: Array<{ hex: string; maxArea: number }> = [
  { hex: '28', maxArea: 2_097_152 }, // 4.0
  { hex: '32', maxArea: 5_652_480 }, // 5.0
  { hex: '33', maxArea: 9_437_184 }, // 5.1
  { hex: '34', maxArea: 9_437_184 }, // 5.2
  { hex: '3c', maxArea: 35_651_584 }, // 6.0
];

async function pickCodec(width: number, height: number, fps: number): Promise<string> {
  const area = width * height;
  const fallback = AVC_LEVELS[AVC_LEVELS.length - 1];
  const levelHexes = AVC_LEVELS.filter((l) => l.maxArea >= area).map((l) => l.hex);
  const profiles = ['6400', '4d00', '4200']; // high → main → baseline

  for (const hex of levelHexes.length ? levelHexes : [fallback.hex]) {
    for (const profile of profiles) {
      const codec = `avc1.${profile}${hex}`;
      try {
        const support = await VideoEncoder.isConfigSupported({ codec, width, height, framerate: fps });
        if (support.supported) return codec;
      } catch {
        /* try next */
      }
    }
  }
  return `avc1.6400${fallback.hex}`;
}

let cancelled = false;

export function cancelExport(): void {
  cancelled = true;
}

export interface ExportResult {
  ok: boolean;
  message: string;
}

export async function runExport(onProgress: (done: number, total: number) => void): Promise<ExportResult> {
  if (!isVideoExportSupported()) {
    return { ok: false, message: 'Export needs a Chromium browser with WebCodecs (https or localhost).' };
  }
  const clips = state.clips.filter((c) => !c.error);
  if (!clips.length) return { ok: false, message: 'Add a clip first.' };

  pause();
  cancelled = false;
  state.exporting = true;
  notify();

  try {
    const fps = state.fps;
    const start = Math.min(state.markIn, state.markOut);
    const end = Math.max(state.markIn, state.markOut);
    const requestedFps = state.exportFps === 'timeline' ? fps : Number(state.exportFps);
    const outFps = Math.min(requestedFps, fps);
    const rangeSec = (end - start + 1) / fps;
    const frameCount = Math.max(1, Math.round(rangeSec * outFps));

    // H.264 4:2:0 needs even dimensions.
    const dims = exportDims();
    const width = dims.w - (dims.w % 2);
    const height = dims.h - (dims.h % 2);

    const scratch = document.createElement('canvas');
    scratch.width = width;
    scratch.height = height;
    const ctx = scratch.getContext('2d', { alpha: false })!;

    const muxer = new Muxer({
      target: new ArrayBufferTarget(),
      // mp4-muxer wants an integer rate; it's only a hint — real frame timing
      // comes from the per-frame microsecond timestamps below.
      video: { codec: 'avc', width, height, frameRate: Math.max(1, Math.round(outFps)) },
      fastStart: 'in-memory',
    });

    const codec = await pickCodec(width, height, outFps);

    // Capture async codec errors and surface them at the next checkpoint.
    let encoderError: Error | null = null;
    const encoder = new VideoEncoder({
      output: (chunk, meta) => muxer.addVideoChunk(chunk, meta),
      error: (e) => {
        encoderError = e as Error;
      },
    });

    const bitrate = Math.min(24_000_000, Math.round(width * height * outFps * 0.08));
    encoder.configure({ codec, width, height, bitrate, framerate: outFps });

    const frameDurationUs = 1_000_000 / outFps;
    const keyEvery = Math.max(1, Math.round(outFps * 2)); // keyframe every ~2s

    try {
      for (let i = 0; i < frameCount; i++) {
        if (cancelled) return { ok: false, message: 'Export cancelled.' };
        if (encoderError) throw encoderError;

        const frame = start + (i / outFps) * fps;
        await Promise.all(
          clips
            .filter((c) => c.video)
            .map((c) => seekExact(c.video!, clipTimeSec(c, frame)))
        );
        composite(ctx, width, height, false);

        const videoFrame = new VideoFrame(scratch, {
          timestamp: Math.round(i * frameDurationUs),
          duration: Math.round(frameDurationUs),
        });
        encoder.encode(videoFrame, { keyFrame: i % keyEvery === 0 });
        videoFrame.close();

        onProgress(i + 1, frameCount);

        // Yield so the encoder queue drains and the UI can paint.
        if (encoder.encodeQueueSize > 8) {
          await new Promise<void>((r) => setTimeout(r, 0));
        }
      }

      await encoder.flush();
      if (encoderError) throw encoderError;
      muxer.finalize();
    } finally {
      if (encoder.state !== 'closed') encoder.close();
    }

    const { buffer } = muxer.target as ArrayBufferTarget;
    const blob = new Blob([buffer], { type: 'video/mp4' });
    downloadBlob(blob, exportFilename(start, end));
    return { ok: true, message: `Exported ${frameCount} frames @ ${outFps}fps (${width}×${height}).` };
  } catch (e) {
    return { ok: false, message: `Export failed: ${e instanceof Error ? e.message : String(e)}` };
  } finally {
    state.exporting = false;
    notify();
  }
}

function exportFilename(start: number, end: number): string {
  const first = state.clips.find((c) => !c.error);
  const stem = (first?.name ?? 'export').replace(/\.[^.]+$/, '').replace(/[^\w.-]+/g, '_');
  const layout = state.layout === 'single' ? '' : `_${state.layout}`;
  return `${stem}_f${start}-${end}${layout}.mp4`;
}

function downloadBlob(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
}
